/**
 * Konva 9 `Shape.hasFill`·`hasStroke` 캐시 무효화를 프로토타입 리스너로.
 *
 * Konva 9는 두 값을 `_calculate`로 캐시하면서, 도형마다 처음 부를 때 의존 속성(fill·stroke 등 9개) 변경 리스너를
 * 인스턴스에 `on()`으로 등록한다. 첫 그리기(`_useBufferCanvas`)에서 도형 수만큼 문자열 조합·분해·리스너 배열 생성이
 * 일어나 A21s 층 진입 첫 그리기 프레임의 ≈30~44ms를 차지했다.
 * Konva가 그림자·패턴 캐시에 쓰는 방식(`Shape.prototype`에 리스너 1개)으로 같은 이벤트에 같은 캐시를 비운다.
 * 캐시 키·계산식은 원본과 같아 결과가 같고, 리스너가 이벤트 이름 기준이라 인스턴스 `off()`에도 풀리지 않는다.
 */
import Konva from 'konva'

interface ShapeInternals {
  _getCache<T>(attr: string, privateGetter: () => T): T
  _clearCache(attr: string): void
  fillEnabled(): boolean
  fill(): unknown
  fillPatternImage(): unknown
  fillLinearGradientColorStops(): unknown
  fillRadialGradientColorStops(): unknown
  strokeEnabled(): boolean
  strokeWidth(): number
  stroke(): unknown
  strokeLinearGradientColorStops(): unknown
}
type ShapeProto = {
  hasFill(this: ShapeInternals): boolean
  hasStroke(this: ShapeInternals): boolean
  on(this: ShapeProto, evtStr: string, handler: (this: ShapeInternals) => void): void
}

// 원본 Shape.hasFill/hasStroke의 의존 속성·계산식 그대로(Konva 9.3)
const FILL_DEPS = [
  'fillEnabled',
  'fill',
  'fillPatternImage',
  'fillLinearGradientColorStops',
  'fillRadialGradientColorStops',
]
const STROKE_DEPS = ['strokeEnabled', 'strokeWidth', 'stroke', 'strokeLinearGradientColorStops']
const changeEvents = (deps: string[]): string => deps.map(dep => `${dep}Change.konva`).join(' ')

function computeHasFill(this: ShapeInternals): boolean {
  return (
    this.fillEnabled() &&
    !!(
      this.fill() ||
      this.fillPatternImage() ||
      this.fillLinearGradientColorStops() ||
      this.fillRadialGradientColorStops()
    )
  )
}
// 원본처럼 strokeWidth 0이면 0을 돌려준다(호출부는 참·거짓으로만 쓴다)
function computeHasStroke(this: ShapeInternals): boolean {
  return (this.strokeEnabled() &&
    this.strokeWidth() &&
    !!(this.stroke() || this.strokeLinearGradientColorStops())) as boolean
}

const shapeProto = Konva.Shape.prototype as unknown as ShapeProto

// Konva 자신의 그림자 캐시와 같은 등록(Shape.prototype은 자기 eventListeners를 가진다)
shapeProto.on.call(shapeProto, changeEvents(FILL_DEPS), function (this: ShapeInternals) {
  this._clearCache('hasFill')
})
shapeProto.on.call(shapeProto, changeEvents(STROKE_DEPS), function (this: ShapeInternals) {
  this._clearCache('hasStroke')
})

shapeProto.hasFill = function (this: ShapeInternals): boolean {
  return this._getCache('hasFill', computeHasFill)
}
shapeProto.hasStroke = function (this: ShapeInternals): boolean {
  return this._getCache('hasStroke', computeHasStroke)
}
